import { PromptInput } from "./PromptInput";
import type { Country } from "./Billing";

type Suggestion = {
  icon: string;
  tag: string;
  text: string;
  color: string;
  bg: string;
  border: string;
  dot: string;
};

const DEFAULT_SUGGESTIONS_BY_COUNTRY: Record<Country, Suggestion[]> = {
  india: [
    {
      icon: "🔄",
      tag: "P2P Lending",
      text: "Build a peer-to-peer lending platform with lender module, borrower module, admin dashboard, wallet system, escrow management, credit scoring APIs, KYC/AML verification, secure payment flows, repayment tracking, and scalable architecture following RBI guidelines",
      color: "#2563EB",
      bg: "#EFF6FF",
      border: "#BFDBFE",
      dot: "#3B82F6",
    },
    {
      icon: "🏦",
      tag: "Core Banking",
      text: "Build a core banking system with account management, transaction processing, loan origination, deposit products, interest calculation, GL integration, and RBI regulatory reporting",
      color: "#059669",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      dot: "#10B981",
    },
    {
      icon: "💳",
      tag: "Digital Banking",
      text: "Build a digital banking super-app with retail banking, UPI payments, fund transfers, account statements, KYC onboarding, and personalised financial insights following RBI guidelines",
      color: "#7C3AED",
      bg: "#F5F3FF",
      border: "#DDD6FE",
      dot: "#8B5CF6",
    },
    {
      icon: "📊",
      tag: "NBFC Platform",
      text: "Build an NBFC lending platform with loan origination, credit bureau integration, collections management, co-lending workflows, ALM reporting, and RBI NBFC compliance automation",
      color: "#D97706",
      bg: "#FFFBEB",
      border: "#FDE68A",
      dot: "#F59E0B",
    },
  ],
  uae: [
    {
      icon: "🏦",
      tag: "Digital Banking UAE",
      text: "Build a CBUAE-compliant digital banking platform with retail accounts, UAE Pass KYC, UAEFTS transfers, open banking APIs, and real-time transaction monitoring",
      color: "#2563EB",
      bg: "#EFF6FF",
      border: "#BFDBFE",
      dot: "#3B82F6",
    },
    {
      icon: "☪️",
      tag: "Islamic Finance",
      text: "Build an Islamic banking platform with Murabaha, Ijara, and Musharaka products, Sharia compliance engine, profit calculation, and CBUAE regulatory reporting",
      color: "#059669",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      dot: "#10B981",
    },
    {
      icon: "💱",
      tag: "Exchange House",
      text: "Build a UAE exchange house platform with remittance management, forex rates, AML/CFT compliance, CBUAE reporting, and multi-currency wallet",
      color: "#7C3AED",
      bg: "#F5F3FF",
      border: "#DDD6FE",
      dot: "#8B5CF6",
    },
    {
      icon: "📊",
      tag: "DIFC Fintech",
      text: "Build a DFSA-regulated fintech platform with investment management, robo-advisory, KYC/AML, and DIFC regulatory compliance reporting",
      color: "#D97706",
      bg: "#FFFBEB",
      border: "#FDE68A",
      dot: "#F59E0B",
    },
  ],
  saudi: [
    {
      icon: "🏦",
      tag: "Digital Banking KSA",
      text: "Build a SAMA-compliant digital banking platform with retail accounts, Absher KYC, SARIE transfers, open banking APIs, and Vision 2030 fintech compliance",
      color: "#2563EB",
      bg: "#EFF6FF",
      border: "#BFDBFE",
      dot: "#3B82F6",
    },
    {
      icon: "☪️",
      tag: "Islamic Banking",
      text: "Build a Sharia-compliant Islamic banking platform with Murabaha, Tawarruq, and Sukuk products, profit calculation engine, and SAMA regulatory reporting",
      color: "#059669",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      dot: "#10B981",
    },
    {
      icon: "💳",
      tag: "BNPL Platform",
      text: "Build a SAMA-licensed Buy Now Pay Later platform with merchant onboarding, credit scoring, instalment management, and Saudi consumer finance compliance",
      color: "#7C3AED",
      bg: "#F5F3FF",
      border: "#DDD6FE",
      dot: "#8B5CF6",
    },
    {
      icon: "📊",
      tag: "CMA Investment",
      text: "Build a CMA-regulated investment platform with portfolio management, Tadawul integration, robo-advisory, KYC/AML, and Saudi capital market compliance",
      color: "#D97706",
      bg: "#FFFBEB",
      border: "#FDE68A",
      dot: "#F59E0B",
    },
  ],
};

const DEFAULT_SUGGESTIONS: Suggestion[] = [
  {
    icon: "🔄",
    tag: "P2P Lending",
    text: "Build a peer-to-peer lending platform with lender module, borrower module, admin dashboard, wallet system, escrow management, credit scoring APIs, KYC/AML verification, secure payment flows, repayment tracking, and scalable architecture following RBI guidelines",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#3B82F6",
  },
  {
    icon: "🏦",
    tag: "Core Banking",
    text: "Build a core banking system with account management, transaction processing, loan origination, deposit products, interest calculation, GL integration, and RBI regulatory reporting",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
    dot: "#10B981",
  },
  {
    icon: "💳",
    tag: "Digital Banking",
    text: "Build a digital banking super-app with retail banking, UPI payments, fund transfers, account statements, KYC onboarding, and personalised financial insights following RBI guidelines",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "#DDD6FE",
    dot: "#8B5CF6",
  },
  {
    icon: "📊",
    tag: "NBFC Platform",
    text: "Build an NBFC lending platform with loan origination, credit bureau integration, collections management, co-lending workflows, ALM reporting, and RBI NBFC compliance automation",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    dot: "#F59E0B",
  },
];

const BANK_CONTENT: Record<
  string,
  { title: string; desc: string; suggestions: Suggestion[] }
> = {
"Finance Companies": {
  title: "Build apps for Finance Companies (UAE)",
  desc: "Build CBUAE-licensed finance company platforms — personal finance, auto finance, SME lending, collections, and regulatory compliance.",
  suggestions: [
    { icon: "🏠", tag: "Personal Finance", text: "Build a UAE CBUAE-licensed personal finance platform with loan origination, Al Etihad Credit Bureau integration, instalment schedule management, early settlement calculation, arrears management, and CBUAE consumer finance compliance", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "🚗", tag: "Auto Finance", text: "Build a UAE auto finance platform with vehicle valuation via dealership integration, hire purchase and lease origination, RTA registration linkage, instalment tracking, early settlement, and CBUAE finance company reporting", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "🏭", tag: "SME Finance", text: "Build a UAE SME finance platform with business loan origination, trade finance facilities, invoice discounting, cash flow underwriting, collateral management, and CBUAE MSME lending compliance reporting", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📞", tag: "Collections Management", text: "Build a collections and recovery management platform for UAE finance companies with delinquency bucket tracking, agent assignment, payment promise management, legal escalation workflows, and CBUAE regulatory reporting", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},
  Banks: {
    title: "Build apps for Banks (UAE)",
    desc: "Build CBUAE-licensed retail and corporate banking platforms — account management, UAE Pass KYC, UAEFTS transfers, open banking APIs, and AML/CFT compliance.",
    suggestions: [
      {
        icon: "🏦",
        tag: "Retail Banking",
        text: "Build a UAE retail banking platform with current and savings accounts, UAE Pass KYC onboarding, UAEFTS fund transfers, account statements, standing instructions, and CBUAE regulatory compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏢",
        tag: "Corporate Banking",
        text: "Build a UAE corporate banking portal with trade finance, letter of credit management, bank guarantees, bulk payroll payments, cash pooling, relationship manager dashboard, and CBUAE reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💹",
        tag: "Treasury Management",
        text: "Build a UAE bank treasury system with forex dealing, money market operations, investment portfolio management, ALM reporting, liquidity monitoring, and CBUAE prudential compliance",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "CBUAE Compliance",
        text: "Build a CBUAE regulatory compliance portal for UAE banks with AML/CFT transaction monitoring, open banking compliance, Basel III reporting, and automated regulatory return generation",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Representative Office": {
    title: "Build apps for Representative Offices (UAE)",
    desc: "Build platforms for foreign bank representative offices in UAE — client liaison, market intelligence, CBUAE reporting, and cross-border referral management.",
    suggestions: [
      {
        icon: "🤝",
        tag: "Client Liaison Portal",
        text: "Build a client liaison and relationship management portal for a UAE representative office with contact management, meeting scheduling, interaction history, and lead referral tracking to the parent bank",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "📋",
        tag: "CBUAE Reporting",
        text: "Build a CBUAE regulatory reporting system for foreign bank representative offices with activity reporting, annual compliance filings, office expenditure tracking, and automated return generation",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🌍",
        tag: "Cross-Border Referral",
        text: "Build a cross-border client referral and pipeline management system for UAE representative offices with deal tracking, parent bank coordination, referral status updates, and revenue attribution reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📈",
        tag: "Market Intelligence",
        text: "Build a UAE market intelligence and research portal for representative offices with local economic data, competitor analysis, regulatory update tracking, and market entry feasibility reporting for the parent bank",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Card Scheme": {
    title: "Build apps for Card Schemes (UAE)",
    desc: "Build UAE card scheme operator platforms — scheme membership management, interchange management, clearing and settlement, dispute resolution, and CBUAE compliance.",
    suggestions: [
      {
        icon: "💳",
        tag: "Scheme Membership",
        text: "Build a card scheme membership management platform for a UAE card network with issuer and acquirer onboarding, membership tier management, certification tracking, scheme rule distribution, and member portal",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔄",
        tag: "Clearing & Settlement",
        text: "Build a card scheme clearing and settlement engine for UAE with multilateral netting, settlement file generation, nostro reconciliation, failed transaction handling, and real-time settlement dashboards",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "⚖️",
        tag: "Dispute & Chargeback",
        text: "Build a card scheme dispute and chargeback management system with dispute intake, reason code management, arbitration workflows, member notifications, resolution tracking, and CBUAE compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Interchange Management",
        text: "Build an interchange fee management platform for a UAE card scheme with interchange rate configuration, fee calculation engine, member billing, revenue reporting, and scheme performance analytics",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Monetary Intermediary": {
    title: "Build apps for Monetary Intermediaries (UAE)",
    desc: "Build CBUAE-licensed monetary intermediary platforms — interbank brokerage, money market placement, FX intermediation, and regulatory compliance.",
    suggestions: [
      {
        icon: "🔄",
        tag: "Interbank Brokerage",
        text: "Build an interbank money market brokerage platform for a UAE monetary intermediary with deal capture, counterparty management, deal confirmation, settlement instruction generation, and broker P&L reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💱",
        tag: "FX Intermediation",
        text: "Build an FX intermediation platform for a UAE monetary intermediary with live rate streaming, FX deal matching, counterparty credit limit management, deal blotter, and CBUAE trade reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📋",
        tag: "Placement Management",
        text: "Build a money market placement management system for UAE monetary intermediaries with deposit placement tracking, maturity management, rollover workflows, interest accrual, and liquidity position reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "CBUAE MI Compliance",
        text: "Build a CBUAE regulatory compliance portal for monetary intermediaries with activity reporting, AML/CFT monitoring, transaction reporting, broker conduct compliance, and automated regulatory return filing",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Stored Value Facilities (SVF)": {
    title: "Build apps for Stored Value Facilities (UAE)",
    desc: "Build CBUAE-licensed SVF platforms — e-wallets, prepaid instruments, multi-currency wallets, instant payments, and PSP compliance.",
    suggestions: [
      {
        icon: "💾",
        tag: "E-Wallet Platform",
        text: "Build a CBUAE-licensed stored value e-wallet platform with multi-currency balance management, instant top-up via debit card and bank transfer, peer-to-peer transfers, transaction history, and SVF compliance reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💳",
        tag: "Prepaid Card System",
        text: "Build a prepaid card management system for a UAE SVF with card issuance, balance loading, merchant payment processing, card controls, spend limits, and CBUAE prepaid instrument compliance",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🔄",
        tag: "Merchant Payments",
        text: "Build a merchant payment acceptance platform for a UAE SVF with QR code payments, POS integration, merchant onboarding, settlement management, dispute handling, and transaction reconciliation",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "SVF Compliance Portal",
        text: "Build a CBUAE SVF regulatory compliance portal with float fund management, AML/CFT transaction monitoring, suspicious activity reporting, safeguarding account reconciliation, and automated regulatory returns",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Exchange Business": {
  title: "Build apps for Exchange Business (UAE)",
  desc: "Build CBUAE-licensed exchange house platforms — remittance management, forex trading, WPS payroll, AML/CFT compliance, and multi-currency operations.",
  suggestions: [
    { icon: "💱", tag: "Remittance Platform", text: "Build a UAE exchange house remittance platform with multi-corridor international transfers, beneficiary management, real-time FX rate engine, transfer fee calculation, delivery status tracking, and CBUAE AML/CFT compliance", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "💰", tag: "WPS Payroll", text: "Build a Wages Protection System payroll platform for UAE exchange houses with employer onboarding, bulk salary file processing, CBUAE WPS compliance, SIF file generation, payment status tracking, and employer dashboard", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "🔄", tag: "Forex Counter", text: "Build a forex counter and currency exchange management system for UAE exchange houses with live rate display, currency buy/sell transaction processing, cash inventory management, teller operations, and end-of-day reconciliation", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📊", tag: "AML/CFT Compliance", text: "Build an AML/CFT compliance portal for UAE exchange businesses with real-time transaction monitoring, PEP and sanctions screening, suspicious activity reporting, CBUAE regulatory filings, and compliance officer dashboard", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},
 "Retail Payment Services": {
  title: "Build apps for Retail Payment Services (UAE)",
  desc: "Build CBUAE-licensed retail payment service provider platforms — payment gateway, merchant acquiring, payment aggregation, and PSP compliance.",
  suggestions: [
    { icon: "🌐", tag: "Payment Gateway", text: "Build a UAE retail payment gateway with multi-channel acceptance for web, mobile, and in-store, card processing, 3DS authentication, tokenisation, real-time transaction monitoring, and CBUAE PSP compliance", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "🏪", tag: "Merchant Acquiring", text: "Build a merchant acquiring platform for a UAE PSP with merchant onboarding and KYB, POS terminal management, multi-currency settlement, chargeback handling, merchant analytics dashboard, and CBUAE reporting", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "🔄", tag: "Payment Aggregation", text: "Build a UAE payment aggregation platform with sub-merchant onboarding, multi-PSP routing, split payment processing, consolidated settlement, reconciliation automation, and regulatory compliance reporting", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📊", tag: "PSP Compliance Portal", text: "Build a CBUAE PSP regulatory compliance portal with AML/CFT transaction monitoring, fraud detection rules engine, suspicious transaction reporting, safeguarding compliance, and automated regulatory return submission", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},
"Loan-Based Crowd Funding": {
  title: "Build apps for Loan-Based Crowd Funding (UAE)",
  desc: "Build CBUAE-licensed P2P lending platforms — borrower and lender modules, credit scoring, escrow management, and crowdfunding regulatory compliance.",
  suggestions: [
    { icon: "🤝", tag: "Borrower Platform", text: "Build a UAE loan-based crowdfunding borrower portal with loan application workflows, business financial document upload, credit scoring integration, loan offer comparison, drawdown management, and repayment tracking", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "💰", tag: "Lender Marketplace", text: "Build a UAE P2P lending investor marketplace with lender onboarding and KYC, loan listing and bidding, auto-invest configuration, portfolio performance tracking, secondary market transfers, and return reporting", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "🔒", tag: "Escrow Management", text: "Build an escrow and fund flow management system for a UAE crowdfunding platform with ring-fenced fund accounts, disbursement triggers, repayment collection, lender distribution, default handling, and reconciliation", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📊", tag: "CBUAE CF Compliance", text: "Build a CBUAE crowdfunding regulatory compliance portal with platform exposure limit monitoring, borrower default reporting, lender disclosure management, AML/CFT checks, and automated regulatory return filing", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},
"Large-value Payment System (LVPS)": {
  title: "Build apps for Large-value Payment Systems (UAE)",
  desc: "Build UAE CBUAE-licensed LVPS platforms — RTGS operations, interbank settlement, liquidity management, and systemically important payment infrastructure.",
  suggestions: [
    { icon: "🏦", tag: "RTGS Operations", text: "Build a UAE RTGS operations management platform with real-time gross settlement processing, payment queue management, settlement finality confirmation, participant connectivity monitoring, and CBUAE LVPS compliance reporting", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "💧", tag: "Liquidity Management", text: "Build an intraday liquidity management system for UAE LVPS participants with real-time liquidity position monitoring, collateral management, intraday credit facility tracking, gridlock resolution, and end-of-day settlement", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "🔄", tag: "Participant Management", text: "Build a LVPS participant management portal for UAE with direct and indirect participant onboarding, settlement account management, bilateral and multilateral limit configuration, and participant reporting", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📊", tag: "Systemic Risk Monitoring", text: "Build a systemic risk and oversight monitoring dashboard for UAE LVPS with settlement throughput analytics, payment concentration monitoring, stress scenario simulation, and CBUAE oversight reporting", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},
"Retail Payment System": {
  title: "Build apps for Retail Payment Systems (UAE)",
  desc: "Build UAE CBUAE-licensed retail payment infrastructure — ACH batch processing, instant payment rails, direct debit schemes, and payment system operations.",
  suggestions: [
    { icon: "⚡", tag: "Instant Payment Rail", text: "Build a UAE instant retail payment rail with real-time payment processing, 24/7 availability, ISO 20022 messaging, participant connectivity, payment confirmation, and CBUAE retail payment system compliance", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "🔄", tag: "ACH Batch Processing", text: "Build an ACH batch payment processing system for UAE retail payments with credit and debit batch file processing, multilateral netting, settlement file generation, return handling, and participant reconciliation", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "📋", tag: "Direct Debit Scheme", text: "Build a UAE direct debit scheme management platform with mandate registration, collection file processing, mandate amendment and cancellation, indemnity claim management, and scheme rule enforcement", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📊", tag: "Payment System Ops", text: "Build a retail payment system operations and oversight portal for UAE with transaction volume monitoring, participant performance dashboards, incident management, scheme reporting, and CBUAE regulatory returns", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},
 "Open Finance": {
  title: "Build apps for Open Finance (UAE)",
  desc: "Build CBUAE open finance framework platforms — account aggregation, consent management, third-party provider APIs, and open banking infrastructure.",
  suggestions: [
    { icon: "🌐", tag: "Account Aggregation", text: "Build a UAE open finance account aggregation platform with multi-bank data connectivity, CBUAE open finance API integration, transaction categorisation, net worth dashboard, and customer financial health insights", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", dot: "#3B82F6" },
    { icon: "🔐", tag: "Consent Management", text: "Build a UAE open finance consent management hub with customer consent capture, granular permission management, consent lifecycle tracking, TPP authorisation flows, revocation handling, and CBUAE compliance audit trails", color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", dot: "#10B981" },
    { icon: "🔗", tag: "TPP API Gateway", text: "Build a third-party provider API gateway for UAE open finance with OAuth 2.0 and FAPI security, account information APIs, payment initiation APIs, TPP onboarding and certification, rate limiting, and developer portal", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", dot: "#8B5CF6" },
    { icon: "📊", tag: "Open Finance Compliance", text: "Build a CBUAE open finance regulatory compliance portal with API performance monitoring, TPP activity reporting, data sharing audit logs, incident reporting, and automated open finance framework return generation", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", dot: "#F59E0B" },
  ],
},


  // ── Saudi Banks ──
  "Local Banks": {
    title: "Build apps for Local Banks (Saudi Arabia)",
    desc: "Build SAMA-licensed retail and corporate banking platforms — Absher KYC, SARIE transfers, open banking APIs, Vision 2030 fintech compliance.",
    suggestions: [
      {
        icon: "🏦",
        tag: "Digital Banking KSA",
        text: "Build a SAMA-compliant digital banking platform for a Saudi commercial bank with retail accounts, Absher KYC, SARIE transfers, open banking APIs, and Vision 2030 fintech compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏢",
        tag: "Corporate Banking KSA",
        text: "Build a Saudi corporate banking portal with trade finance, LC management, bank guarantees, cash management, bulk payments, and SAMA regulatory reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💳",
        tag: "Retail Banking KSA",
        text: "Build a Saudi retail banking super-app with savings accounts, personal finance, credit cards, Absher onboarding, and personalised financial insights",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "SAMA Compliance",
        text: "Build a SAMA regulatory compliance and reporting portal with AML/CFT monitoring, open banking compliance, and automated regulatory return generation",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Digital Banks": {
    title: "Build apps for Digital Banks (Saudi Arabia)",
    desc: "Build SAMA-licensed Saudi digital banking and fintech platforms — mobile-first onboarding, Absher KYC, instant payments, and Vision 2030 fintech compliance.",
    suggestions: [
      {
        icon: "📱",
        tag: "Neobank KSA",
        text: "Build a Saudi neobank mobile app with Absher KYC, instant account opening, SARIE payments, spending analytics, and SAMA digital bank compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔄",
        tag: "Open Banking KSA",
        text: "Build a Saudi open banking platform with SAMA open banking framework compliance, third-party provider APIs, consent management, and account aggregation",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💳",
        tag: "BNPL KSA",
        text: "Build a SAMA-licensed Saudi Buy Now Pay Later platform with merchant onboarding, credit scoring, instalment management, and consumer finance compliance",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Fintech Reporting",
        text: "Build a SAMA fintech regulatory reporting portal with automated returns, AML/CFT monitoring, and Vision 2030 fintech compliance dashboards",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Money Changers": {
    title: "Build apps for Money Changers (Saudi Arabia)",
    desc: "Build SAMA-licensed Saudi money changer platforms — remittance management, forex rates, AML/CFT compliance, and multi-currency transfers.",
    suggestions: [
      {
        icon: "💱",
        tag: "Remittance KSA",
        text: "Build a Saudi money exchange remittance platform with multi-corridor transfers, beneficiary management, real-time FX rates, AML/CFT compliance, and SAMA reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔄",
        tag: "Forex KSA",
        text: "Build a forex management system for Saudi exchange companies with live rate feeds, currency exchange, hedging tools, and SAMA regulatory compliance",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📱",
        tag: "Agent Network KSA",
        text: "Build a Saudi exchange company agent network platform with agent onboarding, transaction limits, commission tracking, and AML monitoring",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "AML/CFT KSA",
        text: "Build an AML/CFT compliance portal for Saudi exchange companies with transaction monitoring, suspicious activity reporting, and SAMA regulatory filings",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Capital Market Institutions": {
    title: "Build apps for Capital Market Institutions (Saudi Arabia)",
    desc: "Build CMA-regulated Saudi capital market platforms — portfolio management, Tadawul integration, robo-advisory, KYC/AML, and Saudi capital market compliance.",
    suggestions: [
      {
        icon: "📈",
        tag: "CMA Investment",
        text: "Build a CMA-regulated investment platform with portfolio management, Tadawul integration, robo-advisory, KYC/AML, and Saudi capital market compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🤖",
        tag: "Robo-Advisory KSA",
        text: "Build a Saudi robo-advisory platform with risk profiling, automated portfolio construction, rebalancing, and CMA-compliant investment management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💼",
        tag: "Wealth Management KSA",
        text: "Build a Saudi wealth management platform with HNI client onboarding, portfolio tracking, Sukuk investments, and CMA regulatory compliance",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "CMA Compliance",
        text: "Build a CMA regulatory compliance portal for Saudi investment firms with trade reporting, KYC/AML, and automated regulatory return generation",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Development Finance Institutions": {
    title: "Build apps for Development Finance Institutions (Saudi)",
    desc: "Build Vision 2030-aligned Saudi development finance platforms — project finance, SME lending, infrastructure funding, and SAMA regulatory compliance.",
    suggestions: [
      {
        icon: "🏗️",
        tag: "Project Finance KSA",
        text: "Build a Saudi development finance project management system with loan disbursement, milestone tracking, escrow management, and Vision 2030 compliance reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏢",
        tag: "SME Development",
        text: "Build an SME development finance platform with loan origination, Kafalah guarantee integration, credit scoring, and SAMA SME lending compliance",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📊",
        tag: "Vision 2030 Reporting",
        text: "Build a Vision 2030 impact reporting portal for Saudi DFIs with project tracking, economic impact metrics, and regulatory compliance dashboards",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🔍",
        tag: "Due Diligence KSA",
        text: "Build a due diligence and credit appraisal system for Saudi infrastructure projects with risk scoring, document management, and approval workflows",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Branches of Foreign Banks": {
    title: "Build apps for Branches of Foreign Banks (Saudi Arabia)",
    desc: "Build platforms for international banks operating in Saudi Arabia — corporate banking, trade finance, SAMA compliance, and cross-border treasury operations.",
    suggestions: [
      {
        icon: "🌍",
        tag: "Cross-Border Payments",
        text: "Build a cross-border payment platform for a foreign bank branch in Saudi Arabia with SWIFT integration, multi-currency accounts, FX management, and SAMA compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💼",
        tag: "Trade Finance KSA",
        text: "Build a trade finance platform for Saudi foreign bank branches with LC issuance, documentary collections, bank guarantees, and SAMA compliance",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💱",
        tag: "Treasury Operations",
        text: "Build a treasury management system for Saudi foreign bank branches with forex dealing, money market operations, and SAMA regulatory reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Branch Reporting KSA",
        text: "Build a branch regulatory reporting portal for Saudi foreign banks with SAMA returns, AML/CFT filings, and automated compliance dashboards",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Payment Service Providers": {
    title: "Build apps for Payment Service Providers (Saudi Arabia)",
    desc: "Build SAMA-licensed Saudi payment service provider platforms — e-wallets, payment aggregation, open banking, and fintech compliance under Vision 2030.",
    suggestions: [
      {
        icon: "📱",
        tag: "E-Wallet KSA",
        text: "Build a SAMA-licensed Saudi e-wallet with multi-currency support, instant transfers, SARIE payments, bill payments, loyalty rewards, and PSP compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔄",
        tag: "Payment Aggregator",
        text: "Build a SAMA-licensed payment aggregation platform with merchant onboarding, multi-gateway routing, settlement management, and regulatory reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🌐",
        tag: "Open Banking KSA",
        text: "Build a Saudi open banking platform with SAMA open banking framework compliance, third-party provider APIs, consent management, and account aggregation",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Fintech Compliance",
        text: "Build a SAMA fintech regulatory compliance portal with automated PSP returns, AML/CFT monitoring, and Vision 2030 fintech reporting dashboards",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  // ── Saudi Finance Companies (SAMA) ──
  "SAMA Finance Companies": {
    title: "Build apps for Finance Companies (Saudi Arabia)",
    desc: "Build SAMA-licensed finance company platforms — real estate finance, consumer finance, SME lending, BNPL, microfinance, and debt-based crowdfunding under Saudi regulations.",
    suggestions: [
      {
        icon: "🏠",
        tag: "Real Estate Finance",
        text: "Build a SAMA-licensed Saudi real estate finance platform with mortgage origination, Absher KYC, Aqarat property valuation integration, instalment schedule management, early settlement calculation, arrears management, and SAMA real estate finance compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💳",
        tag: "Consumer Finance KSA",
        text: "Build a SAMA-licensed Saudi consumer finance platform with personal loan origination, SIMAH credit bureau integration, instalment tracking, early settlement, debt restructuring workflows, and SAMA consumer finance regulatory compliance",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🏭",
        tag: "SME Finance KSA",
        text: "Build a SAMA-licensed Saudi SME finance platform with business loan origination, Kafalah guarantee integration, invoice financing, asset finance, cash flow underwriting, and SAMA SME lending compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📱",
        tag: "BNPL & Microfinance",
        text: "Build a SAMA-licensed Saudi Buy Now Pay Later and consumer microfinance platform with merchant onboarding, credit scoring, instalment management, digital KYC via Absher, SIMAH integration, and SAMA fintech compliance",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "SAMA Finance Companies - Crowdfunding": {
    title: "Build apps for Debt-Based Crowdfunding Platforms (Saudi Arabia)",
    desc: "Build SAMA-licensed debt-based crowdfunding platforms — borrower and lender modules, credit scoring, escrow management, and Saudi crowdfunding regulatory compliance.",
    suggestions: [
      {
        icon: "🤝",
        tag: "Borrower Portal KSA",
        text: "Build a SAMA-licensed Saudi debt-based crowdfunding borrower portal with SME loan application, financial document upload, SIMAH credit check, loan offer comparison, drawdown management, and repayment tracking",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💰",
        tag: "Investor Marketplace KSA",
        text: "Build a Saudi P2P lending investor marketplace with lender onboarding and Absher KYC, loan listing, auto-invest configuration, portfolio performance tracking, and SAMA crowdfunding compliance reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🔒",
        tag: "Escrow & Fund Flow KSA",
        text: "Build an escrow and fund flow management system for a Saudi crowdfunding platform with ring-fenced fund accounts, disbursement triggers, repayment collection, lender distribution, default handling, and reconciliation",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "SAMA CF Compliance",
        text: "Build a SAMA debt-based crowdfunding regulatory compliance portal with platform exposure limit monitoring, borrower default reporting, lender disclosure management, AML/CFT checks, and automated SAMA regulatory return filing",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "SAMA Finance Support Companies": {
    title: "Build apps for Finance Support Companies (Saudi Arabia)",
    desc: "Build SAMA-licensed finance support platforms — finance aggregator services, financial lease contract registries, and digital brokerage platforms under Saudi regulations.",
    suggestions: [
      {
        icon: "🔗",
        tag: "Finance Aggregator KSA",
        text: "Build a SAMA-licensed Saudi finance aggregator platform with multi-lender loan product comparison, customer application routing, lender API integration, credit pre-screening, lead management, and SAMA finance aggregation compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "📋",
        tag: "Lease Registry KSA",
        text: "Build a SAMA-licensed Saudi financial lease contract registry system with lease contract registration, lessor and lessee management, collateral registration, lease termination workflows, and regulatory reporting to SAMA",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💻",
        tag: "Digital Brokerage KSA",
        text: "Build a SAMA-licensed Saudi digital financial brokerage platform with multi-product financial product marketplace, customer onboarding and Absher KYC, product recommendation engine, application processing, and SAMA compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Finance Support Compliance",
        text: "Build a SAMA regulatory compliance portal for Saudi finance support companies with activity reporting, AML/CFT transaction monitoring, customer complaint management, data privacy compliance, and automated SAMA regulatory return generation",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  // ── India Banks ──
  "Local Area Banks": {
    title: "Build apps for Local Area Banks",
    desc: "Create hyper-local banking solutions — branch management, local deposits, community lending, and regional compliance.",
    suggestions: [
      {
        icon: "🏦",
        tag: "Branch Portal",
        text: "Build a branch management portal for a local area bank with teller operations, account opening, local deposit management, and branch-level reporting dashboard",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💰",
        tag: "Community Lending",
        text: "Build a community lending platform for local area banks with loan origination, credit assessment, repayment tracking, and local compliance reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📋",
        tag: "KYC Module",
        text: "Build a KYC and onboarding module for local area bank customers with document upload, verification workflow, and RBI compliance checks",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Reports Dashboard",
        text: "Build a regulatory reporting dashboard for local area banks with RBI returns, audit trails, branch-wise analytics, and automated report generation",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "All India Financial Institutions": {
    title: "Build apps for All India Financial Institutions",
    desc: "Power national-scale financial operations — large project financing, infrastructure lending, and institutional fund management.",
    suggestions: [
      {
        icon: "🏗️",
        tag: "Project Finance",
        text: "Build a project finance management system for All India Financial Institutions with loan disbursement, milestone tracking, escrow management, and compliance reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "📈",
        tag: "Fund Management",
        text: "Build an institutional fund management platform with portfolio tracking, NAV calculation, investor reporting, and regulatory compliance for national financial institutions",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🔍",
        tag: "Due Diligence",
        text: "Build a due diligence and credit appraisal system for large infrastructure projects with risk scoring, document management, and approval workflows",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📜",
        tag: "Compliance Portal",
        text: "Build a compliance and regulatory reporting portal for All India Financial Institutions with SEBI, RBI, and MCA filings, audit management, and automated alerts",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Payments Banks": {
    title: "Build apps for Payments Banks",
    desc: "Design digital-first payment experiences — wallets, UPI, bill payments, and last-mile financial inclusion.",
    suggestions: [
      {
        icon: "💳",
        tag: "Digital Wallet",
        text: "Build a digital wallet platform for a payments bank with UPI integration, bill payments, money transfer, transaction history, and KYC-based onboarding",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔄",
        tag: "P2P Transfer",
        text: "Build a peer-to-peer money transfer system for payments banks with instant settlement, beneficiary management, transaction limits, and fraud detection",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🧾",
        tag: "Bill Payments",
        text: "Build a bill payments and recharge platform for payments banks with BBPS integration, utility payments, subscription management, and payment receipts",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📱",
        tag: "Agent Banking",
        text: "Build a banking correspondent and agent banking app for payments banks with cash-in/cash-out, mini statements, account opening, and commission tracking",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Regional Rural Banks": {
    title: "Build apps for Regional Rural Banks",
    desc: "Empower rural communities — agricultural loans, SHG management, crop insurance, and rural financial inclusion.",
    suggestions: [
      {
        icon: "🌾",
        tag: "Agri Loans",
        text: "Build an agricultural loan management system for regional rural banks with crop loan origination, Kisan Credit Card management, subsidy tracking, and repayment schedules",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "👥",
        tag: "SHG Management",
        text: "Build a Self Help Group management platform for rural banks with group formation, savings tracking, internal lending, and government scheme integration",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🌧️",
        tag: "Crop Insurance",
        text: "Build a crop insurance management system with PMFBY integration, claim filing, weather data integration, farmer onboarding, and payout processing",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🏘️",
        tag: "Rural Outreach",
        text: "Build a rural outreach and financial literacy portal for regional rural banks with scheme awareness, loan eligibility checker, and vernacular language support",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Urban Co-operative Banks": {
    title: "Build apps for Urban Co-operative Banks",
    desc: "Serve city-based cooperative members — member management, share capital, recurring deposits, and urban lending.",
    suggestions: [
      {
        icon: "🏙️",
        tag: "Member Portal",
        text: "Build a member management portal for urban co-operative banks with share capital tracking, membership onboarding, dividend management, and member self-service",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💼",
        tag: "Loan Management",
        text: "Build a loan management system for urban co-operative banks with personal, housing, and vehicle loan origination, EMI tracking, and NPA management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🏦",
        tag: "Deposit Products",
        text: "Build a deposit management system for urban co-operative banks with FD, RD, savings accounts, interest calculation, and maturity alerts",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Board Reporting",
        text: "Build a board and audit reporting dashboard for urban co-operative banks with financial summaries, compliance status, and regulatory return filing",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Non-Banking Financial Companies": {
    title: "Build apps for NBFCs",
    desc: "Accelerate lending operations — loan origination, collections, co-lending, and NBFC-specific compliance.",
    suggestions: [
      {
        icon: "🏠",
        tag: "Loan Origination",
        text: "Build a loan origination system for an NBFC with personal, home, and MSME loan workflows, credit bureau integration, document collection, and approval pipelines",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "📞",
        tag: "Collections",
        text: "Build a collections and recovery management platform for NBFCs with delinquency tracking, agent assignment, payment reconciliation, and legal escalation workflows",
        color: "#DC2626",
        bg: "#FEF2F2",
        border: "#FECACA",
        dot: "#EF4444",
      },
      {
        icon: "🤝",
        tag: "Co-Lending",
        text: "Build a co-lending platform for NBFCs with bank partnership management, loan pool creation, risk sharing, disbursement coordination, and regulatory reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📋",
        tag: "RBI Compliance",
        text: "Build an RBI compliance and reporting portal for NBFCs with CRILC reporting, FIU-IND filings, ALM reports, and automated regulatory return generation",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Asset Reconstruction Companies": {
    title: "Build apps for Asset Reconstruction Companies",
    desc: "Streamline NPA resolution — bad asset acquisition, recovery tracking, security receipts, and SARFAESI compliance.",
    suggestions: [
      {
        icon: "🔄",
        tag: "NPA Acquisition",
        text: "Build an NPA acquisition and portfolio management system for ARCs with asset valuation, bid management, bank negotiations, and acquisition tracking",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "⚖️",
        tag: "Recovery Tracking",
        text: "Build a recovery management platform for ARCs with SARFAESI actions, DRT case tracking, settlement negotiations, and recovery milestone management",
        color: "#DC2626",
        bg: "#FEF2F2",
        border: "#FECACA",
        dot: "#EF4444",
      },
      {
        icon: "📜",
        tag: "Security Receipts",
        text: "Build a security receipt management system for ARCs with SR issuance, investor management, NAV calculation, redemption tracking, and SEBI compliance",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "ARC Dashboard",
        text: "Build an ARC operations dashboard with portfolio health metrics, recovery rate analytics, legal case status, and RBI regulatory reporting",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Small Finance Banks": {
    title: "Build apps for Small Finance Banks",
    desc: "Drive financial inclusion — microfinance, MSME lending, affordable deposits, and underserved segment banking.",
    suggestions: [
      {
        icon: "🏦",
        tag: "Microfinance",
        text: "Build a microfinance loan management system for small finance banks with JLG/SHG lending, field officer app, repayment collection, and portfolio at risk tracking",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏭",
        tag: "MSME Lending",
        text: "Build an MSME loan origination and monitoring platform for small finance banks with GST-based underwriting, cash flow analysis, and collateral management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💳",
        tag: "Savings Products",
        text: "Build a savings and deposit product management system for small finance banks with zero-balance accounts, recurring deposits, and interest payout automation",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📱",
        tag: "Field Officer App",
        text: "Build a field officer mobile app for small finance banks with customer onboarding, loan collection, GPS tracking, offline sync, and daily performance reporting",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Credit Information Companies": {
    title: "Build apps for Credit Information Companies",
    desc: "Build credit intelligence tools — bureau integrations, credit score APIs, dispute management, and analytics.",
    suggestions: [
      {
        icon: "📊",
        tag: "Credit Score API",
        text: "Build a credit score API integration platform with CIBIL, Experian, and CRIF bureau connections, score fetching, report parsing, and lender dashboard",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔍",
        tag: "Dispute Management",
        text: "Build a credit dispute management portal for consumers with dispute filing, bureau communication tracking, resolution timelines, and status notifications",
        color: "#DC2626",
        bg: "#FEF2F2",
        border: "#FECACA",
        dot: "#EF4444",
      },
      {
        icon: "📈",
        tag: "Credit Analytics",
        text: "Build a credit analytics and risk intelligence dashboard with bureau data trends, default prediction models, segment analysis, and lender benchmarking",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🏢",
        tag: "Lender Portal",
        text: "Build a lender portal for credit information companies with bulk bureau pulls, consent management, credit policy configuration, and compliance audit trails",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Commercial Banks": {
    title: "Build apps for Commercial Banks",
    desc: "Power retail and corporate banking — core banking integrations, trade finance, treasury, and digital channels.",
    suggestions: [
      {
        icon: "🔄",
        tag: "P2P Platform",
        text: "Build a peer-to-peer lending platform with lender module, borrower module, admin dashboard, wallet system, escrow management, credit scoring APIs, KYC/AML verification, secure payment flows, repayment tracking, and scalable architecture following RBI guidelines",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏢",
        tag: "Corporate Banking",
        text: "Build a corporate banking portal with trade finance, LC management, bank guarantees, cash management, bulk payments, and relationship manager dashboard",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💹",
        tag: "Treasury",
        text: "Build a treasury management system for commercial banks with forex dealing, money market operations, investment portfolio, ALM, and risk management",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📱",
        tag: "Digital Banking",
        text: "Build a digital banking super-app with retail banking, investments, insurance, credit cards, UPI, and personalised financial insights",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Rural Co-operative Banks": {
    title: "Build apps for Rural Co-operative Banks",
    desc: "Support agricultural communities — crop loans, PACS integration, rural deposits, and cooperative governance.",
    suggestions: [
      {
        icon: "🌱",
        tag: "PACS Integration",
        text: "Build a PACS and district co-operative bank integration platform with loan pass-through, member data sync, subsidy disbursement, and state-level reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🌾",
        tag: "Crop Loans",
        text: "Build a crop loan management system for rural co-operative banks with seasonal loan scheduling, land record integration, insurance linkage, and waiver management",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏘️",
        tag: "Rural Deposits",
        text: "Build a rural deposit management system with village-level savings accounts, recurring deposits, interest calculation, and doorstep banking support",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📋",
        tag: "Governance Portal",
        text: "Build a cooperative governance portal for rural co-operative banks with board meeting management, audit compliance, election management, and NABARD reporting",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
};

const INSURANCE_CONTENT: Record<
  string,
  { title: string; desc: string; suggestions: Suggestion[] }
> = {
  // ── UAE Insurance ──
 "Insurance Companies": {
  title: "Build apps for Insurance Companies (UAE)",
  desc: "Build UAE Insurance Authority licensed insurance company platforms including underwriting, policy administration, claims processing, broker management, reinsurance, and regulatory reporting.",
  suggestions: [
    {
      icon: "📄",
      tag: "Policy Administration",
      text: "Build a UAE insurance policy administration platform with quotation management, underwriting workflows, policy issuance, endorsements, renewals, cancellations, premium calculations, customer self-service portal, document management, and UAE regulatory compliance reporting.",
      color: "#2563EB",
      bg: "#EFF6FF",
      border: "#BFDBFE",
      dot: "#3B82F6",
    },
    {
      icon: "🛡️",
      tag: "Claims Management",
      text: "Build a UAE insurance claims management platform with FNOL (First Notice of Loss), claims registration, surveyor assignment, fraud detection, claims adjudication, settlement workflows, customer claim tracking, document uploads, and automated regulatory reporting.",
      color: "#059669",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      dot: "#10B981",
    },
    {
      icon: "🤝",
      tag: "Broker Portal",
      text: "Build a UAE insurance broker management portal with broker onboarding, quotation generation, policy issuance, lead management, commission calculations, renewal tracking, performance dashboards, customer relationship management, and compliance monitoring.",
      color: "#7C3AED",
      bg: "#F5F3FF",
      border: "#DDD6FE",
      dot: "#8B5CF6",
    },
    {
      icon: "📊",
      tag: "Regulatory Reporting",
      text: "Build a UAE insurance regulatory reporting platform with solvency monitoring, risk management dashboards, audit trails, reinsurance reporting, compliance workflows, automated regulatory submissions, financial reporting, and executive analytics dashboards.",
      color: "#D97706",
      bg: "#FFFBEB",
      border: "#FDE68A",
      dot: "#F59E0B",
    },
  ],
},

"Profession for Insurance": {
  title: "Build apps for Insurance Professionals (UAE)",
  desc: "Build platforms for insurance brokers, agents, consultants, actuaries, and insurance service providers licensed in the UAE.",
  suggestions: [
    {
      icon: "🤝",
      tag: "Broker Management",
      text: "Build a UAE insurance broker management platform with broker onboarding, customer acquisition workflows, policy comparison tools, quotation management, commission tracking, renewal reminders, customer communication tools, and regulatory compliance reporting.",
      color: "#2563EB",
      bg: "#EFF6FF",
      border: "#BFDBFE",
      dot: "#3B82F6",
    },
    {
      icon: "👨‍💼",
      tag: "Agent Portal",
      text: "Build a UAE insurance agent portal with lead management, policy sales workflows, customer onboarding, quotation generation, commission tracking, renewal alerts, performance analytics, and mobile-first agent productivity tools.",
      color: "#059669",
      bg: "#ECFDF5",
      border: "#A7F3D0",
      dot: "#10B981",
    },
    {
      icon: "📈",
      tag: "Actuarial Analytics",
      text: "Build an actuarial analytics platform for UAE insurance professionals with risk modeling, mortality and claims analysis, pricing calculations, reserve estimation, forecasting dashboards, regulatory reporting, and advanced statistical modeling tools.",
      color: "#7C3AED",
      bg: "#F5F3FF",
      border: "#DDD6FE",
      dot: "#8B5CF6",
    },
    {
      icon: "📋",
      tag: "Compliance Management",
      text: "Build an insurance compliance management platform with licensing management, audit workflows, regulatory filing automation, policy governance, risk assessments, compliance monitoring dashboards, document repositories, and UAE regulatory reporting.",
      color: "#D97706",
      bg: "#FFFBEB",
      border: "#FDE68A",
      dot: "#F59E0B",
    },
  ],
},
  // ── Saudi Insurance (Insurance Authority of Saudi Arabia) ──
  "Insurance Companies KSA": {
    title: "Build apps for Insurance Companies (Saudi Arabia)",
    desc: "Build IA-licensed Saudi cooperative insurance (Takaful) platforms — policy administration, claims management, product lines including life, health, motor and general Takaful, and Insurance Authority regulatory compliance.",
    suggestions: [
      {
        icon: "📄",
        tag: "Policy Administration KSA",
        text: "Build a Saudi cooperative insurance policy administration platform with Takaful quotation management, underwriting workflows, policy issuance, endorsements, renewals, cancellations, contribution calculations, customer portal, and Insurance Authority compliance reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🛡️",
        tag: "Claims Management KSA",
        text: "Build a Saudi insurance claims management platform with FNOL, claims registration, Najm integration for motor claims, TPA connectivity for health claims, fraud detection, claims adjudication, settlement workflows, and IA regulatory reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🕌",
        tag: "Takaful Surplus Distribution",
        text: "Build a Saudi Takaful fund management system with participant contribution management, investment portfolio tracking, Sharia-compliant surplus distribution engine, wakala fee calculation, and Insurance Authority cooperative insurance compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "IA Regulatory Reporting",
        text: "Build a Saudi Insurance Authority regulatory reporting platform with solvency margin monitoring, risk-based capital reporting, compliance dashboards, automated IA return filing, reinsurance reporting, and Vision 2030 insurance sector analytics",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Reinsurance Companies KSA": {
    title: "Build apps for Reinsurance Companies (Saudi Arabia)",
    desc: "Build IA-licensed Saudi reinsurance platforms — treaty and facultative reinsurance management, cedant management, claims recovery, and Insurance Authority compliance.",
    suggestions: [
      {
        icon: "🔄",
        tag: "Treaty Reinsurance KSA",
        text: "Build a Saudi reinsurance treaty management platform with proportional and non-proportional treaty configuration, cedant onboarding, premium bordereaux processing, claims recovery tracking, and Insurance Authority reinsurance compliance reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "📋",
        tag: "Facultative Reinsurance",
        text: "Build a facultative reinsurance management system for Saudi reinsurers with risk submission workflows, capacity management, slip documentation, bound confirmation, premium collection, and claims management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "💹",
        tag: "Cedant Management KSA",
        text: "Build a cedant relationship management platform for Saudi reinsurers with cedant onboarding, account management, portfolio analytics, loss ratio monitoring, renewal tracking, and IA compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Reinsurance Compliance",
        text: "Build a Saudi reinsurance regulatory compliance portal with IA return filing, solvency reporting, reinsurance placement reporting, retrocession management, and Insurance Authority oversight dashboards",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Insurance Brokers KSA": {
    title: "Build apps for Insurance Brokers (Saudi Arabia)",
    desc: "Build IA-licensed Saudi insurance brokerage platforms — broker management, multi-insurer quotation, policy placement, commission management, and Insurance Authority compliance.",
    suggestions: [
      {
        icon: "🤝",
        tag: "Broker Portal KSA",
        text: "Build a Saudi insurance broker management platform with broker onboarding and IA licensing tracking, multi-insurer quotation engine, policy placement, customer management, commission calculations, renewal reminders, and compliance monitoring",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "💼",
        tag: "Corporate Insurance Brokerage",
        text: "Build a Saudi corporate insurance brokerage platform with large-account management, risk placement workflows, manuscript policy handling, employee benefits administration, and IA regulatory reporting",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📱",
        tag: "Digital Brokerage KSA",
        text: "Build a Saudi digital insurance brokerage platform with Absher-integrated KYC, online multi-product comparison, instant quotation, digital policy issuance, online claims reporting, and IA-compliant customer portal",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Broker Compliance KSA",
        text: "Build a Saudi insurance broker regulatory compliance portal with IA license renewal tracking, E&O insurance management, customer file management, complaints handling, anti-fraud monitoring, and automated IA return filing",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Bancassurance & Agents KSA": {
    title: "Build apps for Bancassurance & Insurance Agents (Saudi Arabia)",
    desc: "Build IA-licensed Saudi bancassurance and insurance agent platforms — bank-linked insurance distribution, agent management, lead-to-policy workflows, and regulatory compliance.",
    suggestions: [
      {
        icon: "🏦",
        tag: "Bancassurance Platform KSA",
        text: "Build a Saudi bancassurance platform with bank-customer insurance cross-sell workflows, Absher KYC integration, product bundling with bank accounts and loans, premium collection via bank debit, and IA bancassurance compliance reporting",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "👨‍💼",
        tag: "Agent Management KSA",
        text: "Build a Saudi insurance agent management system with agent onboarding and IA license tracking, territory management, lead assignment, policy sales workflows, commission calculation, performance dashboards, and mobile agent app",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📱",
        tag: "Digital Agent KSA",
        text: "Build a Saudi digital insurance agent portal with mobile-first lead management, online quotation generation, digital policy issuance, e-signature integration, renewal alerts, commission statements, and IA compliance tracking",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "Agent Compliance KSA",
        text: "Build a Saudi insurance agent regulatory compliance management portal with IA license management, CE training tracking, complaints handling, misconduct monitoring, commission disclosure reporting, and automated IA filing",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "Insurance Service Providers KSA": {
    title: "Build apps for Insurance Service Providers (Saudi Arabia)",
    desc: "Build IA-licensed Saudi insurance service provider platforms — loss adjusters, health TPAs, claims administrators, actuarial service firms, and insurance technology service providers.",
    suggestions: [
      {
        icon: "🔍",
        tag: "Loss Adjuster Platform KSA",
        text: "Build a Saudi insurance loss adjuster management platform with assignment management, site inspection scheduling, loss assessment documentation, repair cost estimation, settlement recommendation workflows, and IA regulatory compliance",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏥",
        tag: "Health TPA System KSA",
        text: "Build a Saudi health insurance TPA platform with CCHI-compliant hospital network management, pre-authorization workflows, cashless claims adjudication, provider billing, patient eligibility verification, and reimbursement management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📈",
        tag: "Actuarial Services KSA",
        text: "Build a Saudi actuarial services platform with pricing models, reserve estimation, risk analysis dashboards, mortality and claims trend analysis, regulatory solvency calculations, and IA actuarial reporting for cooperative insurance companies",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "📊",
        tag: "ISP Compliance KSA",
        text: "Build a Saudi insurance service provider regulatory compliance portal with IA licensing management, service quality monitoring, customer complaint handling, audit trail management, SLA tracking, and automated Insurance Authority regulatory return filing",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  // ── India Insurance ──
  "Life Insurance": {
    title: "Build apps for Life Insurance",
    desc: "Create intelligent life insurance platforms — policy management, ULIP tracking, claims automation, and IRDAI compliance.",
    suggestions: [
      {
        icon: "🛡️",
        tag: "Policy Management",
        text: "Build a life insurance policy management system with term, ULIP, and endowment products, premium tracking, renewal alerts, nominee management, and IRDAI compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "💰",
        tag: "Claims Portal",
        text: "Build a life insurance claims management portal with death claim filing, document upload, investigation workflow, settlement processing, and fraud detection",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "📊",
        tag: "Agent Dashboard",
        text: "Build a life insurance agent management platform with lead tracking, policy issuance, commission calculation, renewal reminders, and performance analytics",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "📋",
        tag: "Underwriting",
        text: "Build a life insurance underwriting system with risk assessment, medical history evaluation, premium calculation engine, and automated policy approval workflows",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
  "General Insurance": {
    title: "Build apps for General Insurance",
    desc: "Build health, motor, property, marine, engineering, and travel insurance platforms — policy lifecycle, claims management, reinsurance, and regulatory compliance for India, UAE & Saudi.",
    suggestions: [
      {
        icon: "🏥",
        tag: "Health Insurance",
        text: "Build a health insurance platform with policy issuance, cashless hospitalisation, TPA integration, claims adjudication, fraud detection, and IRDAI compliance",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🚗",
        tag: "Motor Insurance",
        text: "Build a motor insurance system with vehicle onboarding, OD and TP policy management, garage network integration, surveyor assignment, and claims settlement",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🏠",
        tag: "Property Insurance",
        text: "Build a property insurance platform with asset valuation, policy issuance, natural disaster claims, reinstatement tracking, and reinsurance management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "✈️",
        tag: "Travel Insurance",
        text: "Build a travel insurance system with trip protection policies, flight delay claims, medical emergency coverage, baggage loss processing, and real-time assistance",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  },
};

const DEFAULT_INSURANCE_SUGGESTIONS_BY_COUNTRY: Record<Country, Suggestion[]> =
  {
    india: [
      {
        icon: "🛡️",
        tag: "Life Insurance",
        text: "Build a life insurance policy management system with term, ULIP, and endowment products, premium tracking, renewal alerts, nominee management, and IRDAI compliance reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🏥",
        tag: "Health Insurance",
        text: "Build a health insurance platform with policy issuance, cashless hospitalisation, TPA integration, claims adjudication, fraud detection, and IRDAI compliance",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🚗",
        tag: "Motor Insurance",
        text: "Build a motor insurance system with vehicle onboarding, OD and TP policy management, garage network integration, surveyor assignment, and claims settlement",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "✈️",
        tag: "Travel Insurance",
        text: "Build a travel insurance system with trip protection policies, flight delay claims, medical emergency coverage, baggage loss processing, and real-time assistance",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
    uae: [
      {
        icon: "🛡️",
        tag: "Takaful Life",
        text: "Build a UAE Takaful life insurance platform with family takaful products, contribution management, surplus distribution, UAE Insurance Authority compliance, and Sharia board reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🏥",
        tag: "Health Insurance UAE",
        text: "Build a UAE mandatory health insurance platform with DHA/HAAD/SEHA compliance, network hospital management, cashless claims, and employer group policy management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🚗",
        tag: "Motor Insurance UAE",
        text: "Build a UAE motor insurance system with RTA-compliant policy issuance, garage network, surveyor assignment, third-party liability, and claims settlement",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔒",
        tag: "General Insurance UAE",
        text: "Build a UAE general insurance platform with property, marine, engineering, and travel insurance products, UAE Insurance Authority compliance, and reinsurance management",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
    saudi: [
      {
        icon: "🛡️",
        tag: "Cooperative Insurance",
        text: "Build a Saudi cooperative (Takaful) life insurance platform with SAMA compliance, contribution management, surplus distribution, and Vision 2030 insurance sector reporting",
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        dot: "#8B5CF6",
      },
      {
        icon: "🏥",
        tag: "Health Insurance KSA",
        text: "Build a Saudi mandatory health insurance platform with CCHI compliance, network hospital management, cashless claims, and employer group policy management",
        color: "#059669",
        bg: "#ECFDF5",
        border: "#A7F3D0",
        dot: "#10B981",
      },
      {
        icon: "🚗",
        tag: "Motor Insurance KSA",
        text: "Build a Saudi motor insurance system with SAMA-compliant policy issuance, Najm integration, garage network, third-party liability, and claims settlement",
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        dot: "#3B82F6",
      },
      {
        icon: "🔒",
        tag: "General Takaful KSA",
        text: "Build a Saudi general takaful platform with property, marine, engineering insurance products, SAMA compliance, and reinsurance management",
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        dot: "#F59E0B",
      },
    ],
  };

const DEFAULT_INSURANCE_SUGGESTIONS: Suggestion[] = [
  {
    icon: "🛡️",
    tag: "Life Insurance",
    text: "Build a life insurance policy management system with term, ULIP, and endowment products, premium tracking, renewal alerts, nominee management, and IRDAI compliance reporting",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "#DDD6FE",
    dot: "#8B5CF6",
  },
  {
    icon: "🏥",
    tag: "Health Insurance",
    text: "Build a health insurance platform with policy issuance, cashless hospitalisation, TPA integration, claims adjudication, fraud detection, and IRDAI compliance",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
    dot: "#10B981",
  },
  {
    icon: "🚗",
    tag: "Motor Insurance",
    text: "Build a motor insurance system with vehicle onboarding, OD and TP policy management, garage network integration, surveyor assignment, and claims settlement",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#3B82F6",
  },
  {
    icon: "✈️",
    tag: "Travel Insurance",
    text: "Build a travel insurance system with trip protection policies, flight delay claims, medical emergency coverage, baggage loss processing, and real-time assistance",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    dot: "#F59E0B",
  },
];

interface LandingViewProps {
  running: boolean;
  onRun: (prompt: string) => void;
  selectedBank: string | null;
  mode: "banking" | "insurance";
  country?: Country;
}

export function LandingView({
  running,
  onRun,
  selectedBank,
  mode,
  country = "india",
}: LandingViewProps) {
  const isInsurance = mode === "insurance";
  const contentMap = isInsurance ? INSURANCE_CONTENT : BANK_CONTENT;
  const defaultSuggestions = isInsurance
    ? (DEFAULT_INSURANCE_SUGGESTIONS_BY_COUNTRY[country] ??
      DEFAULT_INSURANCE_SUGGESTIONS)
    : (DEFAULT_SUGGESTIONS_BY_COUNTRY[country] ?? DEFAULT_SUGGESTIONS);
  const bankContent = selectedBank ? contentMap[selectedBank] : null;
  const suggestions = bankContent?.suggestions ?? defaultSuggestions;
  const brandName = isInsurance ? "INSURVIBE" : "FINVIBE";
  const brandGradient = isInsurance
    ? "linear-gradient(90deg,#7C3AED 0%,#a855f7 55%,#06B6D4 100%)"
    : "linear-gradient(90deg,#3B6FFF 0%,#7C3AED 55%,#06B6D4 100%)";

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8F9FC",
      }}
    >
      {/* ── Scrollable body ── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 32px 16px",
          scrollbarWidth: "none",
        }}
      >
        {/* ──── Hero ──── */}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 16px",
              borderRadius: "100px",
              marginBottom: "14px",
              background: "linear-gradient(135deg,#EEF3FF 0%,#F3EEFF 100%)",
              border: "1px solid #C7D8FF",
              boxShadow: "0 2px 12px rgba(59,111,255,.1)",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#3B6FFF",
                display: "inline-block",
                animation: "fv-pulse 1.6s ease-in-out infinite",
              }}
            />
            <span
              style={{
                fontSize: "10.5px",
                fontWeight: 700,
                color: "#3B6FFF",
                letterSpacing: ".12em",
                textTransform: "uppercase" as const,
              }}
            >
              AI · Powered Code Generator
            </span>
          </div>

          <h1
            style={{
              fontSize: "clamp(26px,3.6vw,38px)",
              fontWeight: 900,
              letterSpacing: "-.04em",
              lineHeight: 1.1,
              color: "#0A0E1A",
              margin: "0 0 8px",
            }}
          >
            {bankContent ? (
              bankContent.title
            ) : (
              <>
                Build your application with{" "}
                <span
                  style={{
                    display: "inline-block",
                    background: brandGradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-.03em",
                  }}
                >
                  {brandName}
                </span>
              </>
            )}
          </h1>

          <p
            style={{
              fontSize: "13.5px",
              color: "#6B7A99",
              lineHeight: 1.6,
              margin: "0 auto 14px",
              maxWidth: "440px",
              fontWeight: 500,
            }}
          >
            {bankContent
              ? bankContent.desc
              : `Describe your app — ${brandName} architects, designs, and generates production-ready code in seconds.`}
          </p>
        </div>

        {/* ──── Suggestion cards ──── */}
        <div
          style={{
            width: "100%",
            maxWidth: "660px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
            marginBottom: "14px",
          }}
        >
          {suggestions.map((s) => (
            <button
              key={s.tag}
              onClick={() => onRun(s.text)}
              disabled={running}
              style={{
                textAlign: "left",
                padding: "13px 14px",
                borderRadius: "14px",
                background: "#FFFFFF",
                border: "1px solid #E8ECF4",
                boxShadow: "0 1px 3px rgba(13,17,23,.05)",
                cursor: running ? "not-allowed" : "pointer",
                opacity: running ? 0.45 : 1,
                transition: "all .18s ease",
              }}
              onMouseEnter={(e) => {
                if (running) return;
                const el = e.currentTarget as HTMLElement;
                el.style.background = s.bg;
                el.style.borderColor = s.border;
                el.style.boxShadow = `0 5px 18px ${s.dot}22`;
                el.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = "#FFFFFF";
                el.style.borderColor = "#E8ECF4";
                el.style.boxShadow = "0 1px 3px rgba(13,17,23,.05)";
                el.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  marginBottom: "6px",
                }}
              >
                <span style={{ fontSize: "15px" }}>{s.icon}</span>
                <span
                  style={{
                    fontSize: "9.5px",
                    fontWeight: 700,
                    letterSpacing: ".1em",
                    textTransform: "uppercase" as const,
                    padding: "2px 8px",
                    borderRadius: "6px",
                    background: s.bg,
                    color: s.color,
                    border: `1px solid ${s.border}`,
                  }}
                >
                  {s.tag}
                </span>
              </div>
              <p
                style={{
                  fontSize: "11px",
                  lineHeight: 1.55,
                  color: "#6B7A99",
                  margin: 0,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical" as const,
                  overflow: "hidden",
                }}
              >
                {s.text}
              </p>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div
          style={{
            width: "100%",
            maxWidth: "660px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ flex: 1, height: "1px", background: "#E8ECF4" }} />
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              color: "#C4CBDA",
              letterSpacing: ".1em",
              textTransform: "uppercase" as const,
              whiteSpace: "nowrap",
            }}
          >
            or describe your own
          </span>
          <div style={{ flex: 1, height: "1px", background: "#E8ECF4" }} />
        </div>
      </div>

      {/* ── Sticky prompt bar ── */}
      <div
        style={{
          flexShrink: 0,
          padding: "12px 32px 16px",
          borderTop: "1px solid #EAECF2",
          background: "#FFFFFF",
          boxShadow: "0 -2px 14px rgba(13,17,40,.06)",
        }}
      >
        <div style={{ maxWidth: "660px", margin: "0 auto" }}>
          <PromptInput onSubmit={onRun} disabled={running} />
        </div>
      </div>

      <style>{`@keyframes fv-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }`}</style>
    </div>
  );
}
